// All-accounts {value, label} options for account/issuer/investor selects,
// backed by the asset-transaction header-options read. Local standard for
// pickers that previously fetched once on mount into component state and
// went stale: tabs never unmount (TabContentWrapper hides them), so an
// account created elsewhere (e.g. the global add) never appeared without a
// hard reload. Spread `onDropdownVisibleChange: onOpenRefetch` onto the
// Select so the list refreshes every time the dropdown opens — the read is
// a cheap id+name list.
import { useCallback, useEffect, useState } from 'react'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { fetchAssetTransactionHeaderOptions } from '../services/assetTransactionsService.ts'

export interface AccountOption {
  value: string
  label: string
}

export function useAccountOptions(enabled: boolean = true): {
  options: AccountOption[]
  refetch: () => Promise<AccountOption[] | null>
  onOpenRefetch: (open: boolean) => void
} {
  const [options, setOptions] = useState<AccountOption[]>([])

  const refetch = useCallback(async (): Promise<AccountOption[] | null> => {
    const token = getAccessToken(store.getState().auth)
    const result = await fetchAssetTransactionHeaderOptions(token)
    if (!result.data) return null
    const next = (result.data.companies || []).map((c) => ({
      value: c.value,
      label: c.label || ''
    }))
    setOptions(next)
    return next
  }, [])

  useEffect(() => {
    if (enabled) void refetch()
  }, [enabled, refetch])

  const onOpenRefetch = useCallback(
    (open: boolean) => {
      if (open) void refetch()
    },
    [refetch]
  )

  return { options, refetch, onOpenRefetch }
}
